// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resources } from '@shared/localizations/Resources';
import { LinkedSystem } from '../misc/LinkedSystem';
import { PositionItem } from '../cache/PositionItem';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { TradingButtonItem } from '../elements/TradingButtonStripe';
import { RactiveTooltip } from '../misc/Decorators';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { OrdersPanelBase } from './OrdersPanelBase';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DynProperty } from '@shared/commons/DynProperty';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { PositionActionEnum } from '@shared/utils/Trading/PositionActionEnum';
import { NumericLinks } from '@shared/commons/cache/OrderParams/order-edit/OrderEditBase';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { IsAllowedResponceReason } from '@shared/commons/IsAllowedResponceReason';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderEntryParameters } from '@shared/utils/OrderEntryParameters';
import { MessageBoxButtonsTextKey, MessageBoxType, showMessageBoxAsync } from '@shared/utils/MessageBoxUtils';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class PositionsPanel extends OrdersPanelBase {
    get selectedPositions() {
        const positions = [];
        const quickTable = this.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return positions;
        }
        const selectedTableItems = quickTable.getSelectedTableItems();
        for (let i = 0; i < selectedTableItems.length; i++) {
            const selectedTableItem = selectedTableItems[i];
            if (!isNullOrUndefined(selectedTableItem.FPosition)) {
                positions.push(selectedTableItem.FPosition);
            }
        }
        return positions;
    }
    constructor() {
        super();
        this.regroupFIFOTimerID = null; // for setTimeout of regrouping FIFO positions when populate #80010
        this.InitShowTotals = null;
        this.InitDisplayTrades = null;
        this.InitShowGropQtyAsTotal = null;
        this.Name = 'PositionsPanel';
        this.headerLocaleKey = 'panel.positions';
        this.moreThanOneTTKey = 'panel.positions.menu.MoreOneSymbolInfoDisabled.tt'; // #95439
        this.noOneTTKey = 'panel.positions.menu.NoSymbolInfoDisabled.tt';
    }
    // #region Overrides
    oncomplete() {
        super.oncomplete();
        this.initContextMenuItemsState();
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        quickTable.OnPaintedPictureButtonClick.Subscribe(this.onTableBtnClick, this);
        quickTable.onTableMouseDown.Subscribe(this.onTableMouseDown, this);
        quickTable.AfterExpandItem.Subscribe(this.onExpandSuperPositionClick, this); // collapse super fifo event #80010
        this.UpdateTradesVisibility(quickTable.additionalFilter); // fifo sub items will show for a moment if not do this
    }
    // #region ApplicationPanelWithTable
    getType() {
        return PanelNames.PositionsPanel;
    }
    populateItemsDirect() {
        super.populateItemsDirect();
        const positions = DataCache.Trading.getAllPositions();
        const positionsCorporateAction = DataCache.Trading.getAllPositionsCorporateAction();
        this.populateItems(positions);
        this.populateItems(positionsCorporateAction);
    }
    createMenuItems() {
        const placedFromVal = PlacedFrom.WEB_POSITIONS_PANEL_CONTEXT_MENU;
        const items = [
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.Modify',
                tag: PositionActionEnum.PositionModifying,
                event: this.action.bind(this, PositionActionEnum.PositionModifying, true, placedFromVal, null)
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.Close',
                tag: PositionActionEnum.ClosePositionMkt,
                svgImage: this.getImgByAction(PositionActionEnum.ClosePositionMkt),
                event: () => {
                    this.closePositionsMkt(this.selectedPositions, placedFromVal);
                }
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.CloseViaOE',
                tag: PositionActionEnum.ClosePositionViaOE,
                svgImage: this.getImgByAction(PositionActionEnum.ClosePositionViaOE),
                visible: !Resources.isHidden('panel.positions.menu.CloseViaOE'),
                event: () => {
                    this.closePositionsViaOE(this.selectedPositions);
                }
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.MutualClose',
                tag: PositionActionEnum.Mutual,
                visible: !Resources.isHidden('panel.positions.menu.MutualClose'),
                event: this.action.bind(this, PositionActionEnum.Mutual, true, placedFromVal)
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.ExercisePosition',
                tag: PositionActionEnum.ExerciseOption,
                visible: false, // по умолчанию видимость отключена. Пункт отображается и активен, если в панели выбрана позиция по опциону с Exercise style = American
                event: this.action.bind(this, PositionActionEnum.ExerciseOption, true, placedFromVal)
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.ExerciseCancel',
                tag: PositionActionEnum.ExerciseCancel,
                visible: false, // по умолчанию видимость отключена. Пункт отображается и активен, если в панели выбрана позиция по опциону со статусом Pending exercise.
                event: this.action.bind(this, PositionActionEnum.ExerciseCancel, true, placedFromVal)
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.ModifyProductType',
                tag: PositionActionEnum.ModifyProductType,
                visible: DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING_MODE_MODIFICATION) && !Resources.isHidden('panel.positions.menu.ModifyProductType'),
                event: this.action.bind(this, PositionActionEnum.ModifyProductType, true, placedFromVal, NumericLinks.ProductType)
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.View',
                svgImage: 'View',
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.positions.menu.DisplayTrades',
                        tag: 'DisplayTrades',
                        canCheck: true,
                        checked: false,
                        visible: !Resources.isHidden('panel.positions.menu.DisplayTrades'),
                        event: this.DisplayTradesStateChange.bind(this)
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.positions.menu.ShowToolbar',
                        canCheck: true,
                        checked: this.ToolbarVisible,
                        visible: !Resources.isHidden('panel.positions.menu.ShowToolbar'),
                        event: this.ShowToolbarStateChange.bind(this)
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.positions.menu.ShowTotals',
                        tag: 'ShowTotals',
                        canCheck: true,
                        checked: false,
                        visible: !Resources.isHidden('contextMenu.Totals.visibility'),
                        event: this.ShowTotalsStateChange.bind(this)
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.positions.menu.ShowGroupQtyAs.locKey',
                        tag: 'ShowGroupQtyAs',
                        canCheck: true,
                        checked: false,
                        visible: !Resources.isHidden('panel.positions.menu.ShowGroupQtyAs'),
                        subitems: [
                            {
                                type: NewContextMenuItemType.Default,
                                text: 'panel.positions.menu.ShowGroupQtyAs.Total',
                                tag: PositionsPanel.ShowGroupQtyAs.Total,
                                canCheck: true,
                                checked: this.ToolbarVisible,
                                event: this.ShowGroupQtyAsStateChange.bind(this)
                            },
                            {
                                type: NewContextMenuItemType.Default,
                                text: 'panel.positions.menu.ShowGroupQtyAs.Net',
                                tag: PositionsPanel.ShowGroupQtyAs.Net,
                                canCheck: true,
                                checked: true,
                                event: this.ShowGroupQtyAsStateChange.bind(this)
                            }
                        ]
                    }
                ]
            }
        ];
        super.NewAddOpeningPanelsCM(items);
        super.AddSymbolInfoContextMenuItemIfNeed(items, true);
        return items;
    }
    GetOpeningPanelsCMLocKeysSet() {
        const keysObj = {};
        keysObj[ApplicationPanelWithTable.OPEN_CHART] = 'panel.positions.showChartMI';
        keysObj[ApplicationPanelWithTable.OPEN_MD] = 'panel.positions.showMDMI';
        keysObj[ApplicationPanelWithTable.OPEN_TS] = 'panel.positions.showTSMI';
        keysObj[ApplicationPanelWithTable.OPEN_OE] = 'panel.positions.showOEMI';
        return keysObj;
    }
    preparePopup() {
        super.preparePopup();
        this.updateAllowedActions();
        this.updateMenuItemsTooltip();
    }
    Properties() {
        const qt = super.getQuickTable();
        const properties = OrdersPanelBase.prototype.Properties.call(this);
        properties.push(new DynProperty('ShowTotals', qt.ShowTotals, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('DisplayTrades', qt.additionalFilter, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('ShowGroupQtyAsTotal', !this.IsShowGroupQtyAsNet(), DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        this.InitShowTotals = DynProperty.getPropValue(newProperties, 'ShowTotals');
        this.InitDisplayTrades = DynProperty.getPropValue(newProperties, 'DisplayTrades');
        this.InitShowGropQtyAsTotal = DynProperty.getPropValue(newProperties, 'ShowGroupQtyAsTotal');
    }
    SetColumnsDefaultDisplayIndex(table) {
        table.columns[PositionItem.SYMBOL_COL_INDEX].displayedIndex = 0;
        table.columns[PositionItem.ACCOUNT_COL_INDEX].displayedIndex = 15;
        table.columns[PositionItem.QUANTITY_COL_INDEX].displayedIndex = 4;
        table.columns[PositionItem.BASIS_COL_INDEX].displayedIndex = 5;
        table.columns[PositionItem.EXPDATE_COL_INDEX].displayedIndex = 7;
        table.columns[PositionItem.STRIKE_COL_INDEX].displayedIndex = 9;
        table.columns[PositionItem.POSNUMBER_COL_INDEX].displayedIndex = 10;
        table.columns[PositionItem.OPERATION_COL_INDEX].displayedIndex = 1;
        table.columns[PositionItem.DATE_TIME_COL_INDEX].displayedIndex = 14;
        table.columns[PositionItem.CUR_PRICE_COL_INDEX].displayedIndex = 6;
        table.columns[PositionItem.SL_COL_INDEX].displayedIndex = 16;
        table.columns[PositionItem.TP_COL_INDEX].displayedIndex = 17;
        table.columns[PositionItem.PROFIT_USD_COL_INDEX].displayedIndex = 8;
        table.columns[PositionItem.SWAPS_COL_INDEX].displayedIndex = 2;
        table.columns[PositionItem.EXPOSITION_USD_COL_INDEX].displayedIndex = 3;
        table.columns[PositionItem.COMISSION_COL_INDEX].displayedIndex = 11;
        table.columns[PositionItem.COMMENTS_COL_INDEX].displayedIndex = 18;
        table.columns[PositionItem.ROUTE_COL_INDEX].displayedIndex = 19;
        table.columns[PositionItem.TYPE_COL_INDEX].displayedIndex = 20;
        table.columns[PositionItem.TRAILING_STOP_COL_INDEX].displayedIndex = 21;
        table.columns[PositionItem.LOGIN_COL_INDEX].displayedIndex = 22;
        table.columns[PositionItem.SYMBOL_DESCRIPTION_COL_INDEX].displayedIndex = 23;
        table.columns[PositionItem.USED_MARGIN_COL_INDEX].displayedIndex = 24;
        table.columns[PositionItem.PL_TICKS_COL_INDEX].displayedIndex = 13;
        table.columns[PositionItem.NET_PL_COL_INDEX].displayedIndex = 12;
        table.columns[PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX].displayedIndex = 13;
        let displayIndex = 99999;
        table.columns[PositionItem.CLOSE_COL_INDEX].displayedIndex = displayIndex++;
        table.columns[PositionItem.CLOSE_VIA_OE_COL_INDEX].displayedIndex = displayIndex++;
    }
    SetColumnsColouringMode(table) {
        super.ProcessSetColumnsColouringMode(table, [PositionItem.CUR_PRICE_COL_INDEX], ColouringModes.Previous);
        super.ProcessSetColumnsColouringMode(table, [
            PositionItem.PROFIT_USD_COL_INDEX,
            PositionItem.COMMENTS_COL_INDEX,
            PositionItem.PL_TICKS_COL_INDEX,
            PositionItem.NET_PL_COL_INDEX,
            PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX
        ], ColouringModes.Signed);
    }
    // #endregion
    // #region OrdersPanelBase
    getDoubleClickActionParams() {
        return {
            action: PositionActionEnum.ClosePositionMkt,
            confirm: true,
            placedFrom: PlacedFrom.WEB_POSITIONS_PANEL_DB_CLICK
        };
    }
    getHotButtonPlacedFrom() {
        return PlacedFrom.WEB_POSITIONS_PANEL_HOT_BTN;
    }
    DCEventsSubscribe() {
        DataCache.OnAddPosition.Subscribe(this.AddPositionEvent, this);
        DataCache.OnUpdatePosition.Subscribe(this.UpdatePositionEvent, this);
        DataCache.OnRemovePosition.Subscribe(this.RemovePositionEvent, this);
        // CorporateAction
        DataCache.OnAddCorporateAction.Subscribe(this.AddPositionEvent, this);
        DataCache.OnUpdateCorporateAction.Subscribe(this.UpdatePositionEvent, this);
        DataCache.OnRemoveCorporateAction.Subscribe(this.RemovePositionEvent, this);
    }
    DCEventsUnSubscribe() {
        DataCache.OnAddPosition.UnSubscribe(this.AddPositionEvent, this);
        DataCache.OnUpdatePosition.UnSubscribe(this.UpdatePositionEvent, this);
        DataCache.OnRemovePosition.UnSubscribe(this.RemovePositionEvent, this);
        // CorporateAction
        DataCache.OnAddCorporateAction.UnSubscribe(this.AddPositionEvent, this);
        DataCache.OnUpdateCorporateAction.UnSubscribe(this.UpdatePositionEvent, this);
        DataCache.OnRemoveCorporateAction.UnSubscribe(this.RemovePositionEvent, this);
    }
    onTableBtnClick(args) {
        var _a;
        const item = (_a = args.row) === null || _a === void 0 ? void 0 : _a.item;
        if (isNullOrUndefined(item)) {
            return;
        }
        if (item.Disabled) {
            RactiveTooltip.showTooltip('IsAllowedResponceReason.OptionPendingExerciseStatus');
            return;
        }
        const position = item.FPosition;
        if (isNullOrUndefined(position)) {
            return;
        }
        let errorText = '';
        switch (args.realColumnIndex) {
            case PositionItem.CLOSE_COL_INDEX:
                const isAllowCloseMarketPosition = IsAllowed.IsPositionClosingAllowed(position, true);
                if (!isAllowCloseMarketPosition.Allowed) {
                    errorText = isAllowCloseMarketPosition.ReasonText;
                }
                else {
                    this.closePositionsMkt([position], PlacedFrom.WEB_POSITIONS_PANEL_DB_CLICK);
                }
                break;
            case PositionItem.CLOSE_VIA_OE_COL_INDEX:
                const isAllowClosePositionViaOe = IsAllowed.IsPositionClosingAllowed(position, false);
                if (!isAllowClosePositionViaOe.Allowed) {
                    errorText = isAllowClosePositionViaOe.ReasonText;
                }
                else {
                    this.closePositionsViaOE([position]);
                }
                break;
        }
        if (isValidString(errorText)) {
            void this.showErrorAsync(errorText);
        }
    }
    showErrorAsync(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const messageBoxParameters = {
                type: MessageBoxType.Warning,
                title: Resources.getResource('MessageBox.ThemeChanged.Title'),
                message: message,
                okText: Resources.getResource(MessageBoxButtonsTextKey.Ok)
            };
            return yield showMessageBoxAsync(messageBoxParameters);
        });
    }
    closePositionsMkt(positions, placedFrom) {
        const selectedOrderIdArr = positions.map((p) => p.PositionId);
        void DataCache.FOrderExecutor.positionAction(PositionActionEnum.ClosePositionMkt, this.isFiltered, selectedOrderIdArr, selectedOrderIdArr, true, placedFrom, undefined);
    }
    closePositionsViaOE(positions) {
        var _a;
        for (const position of positions) {
            (_a = MainWindowManager.Factory) === null || _a === void 0 ? void 0 : _a.addPanel(PanelNames.AdvancedOrderEntry, null, (panel) => {
                const params = new OrderEntryParameters();
                params.account = position.Account;
                params.instrument = position.Instrument;
                params.isLong = position.BuySell !== OperationType.Buy;
                params.quantity = position.Amount;
                params.limitPrice =
                    position.BuySell === OperationType.Buy
                        ? position.Instrument.Level1.GetBid(position.Account)
                        : position.Instrument.Level1.GetAsk(position.Account);
                panel.applyClosePositionViaOEParameters(params);
            });
        }
    }
    IsClosingAllowed(selectedOrderIdArr) {
        const positions = DataCache.Trading.getPositionsById(selectedOrderIdArr);
        let result = null;
        for (const position of positions) {
            const res = IsAllowed.IsPositionClosingAllowed(position, true);
            if (result === null || !res.Allowed) {
                result = res;
            }
        }
        return result;
    }
    createTradingButtonItems() {
        return [
            new TradingButtonItem(PositionActionEnum.All, 'panels.positions.buttons.closeAll', this.isFiltered && this.isFilteredAction(PositionActionEnum.All)),
            new TradingButtonItem(PositionActionEnum.CLXAll, 'panels.positions.buttons.clxAll', this.isFiltered && this.isFilteredAction(PositionActionEnum.CLXAll)),
            new TradingButtonItem(PositionActionEnum.Negative, 'panels.positions.buttons.closeNegative', this.isFiltered && this.isFilteredAction(PositionActionEnum.Negative)),
            new TradingButtonItem(PositionActionEnum.Positive, 'panels.positions.buttons.closePositive', this.isFiltered && this.isFilteredAction(PositionActionEnum.Positive)),
            new TradingButtonItem(PositionActionEnum.ByAccountAndInstrument, 'panels.positions.buttons.closeForInstrument', this.isFiltered && this.isFilteredAction(PositionActionEnum.ByAccountAndInstrument)),
            new TradingButtonItem(PositionActionEnum.Short, 'panels.positions.buttons.closeShort', this.isFiltered && this.isFilteredAction(PositionActionEnum.Short)),
            new TradingButtonItem(PositionActionEnum.Long, 'panels.positions.buttons.closeLong', this.isFiltered && this.isFilteredAction(PositionActionEnum.Long)),
            new TradingButtonItem(PositionActionEnum.ClosePositionMkt, 'panels.positions.buttons.closeSelected', this.isFiltered && this.isFilteredAction(PositionActionEnum.ClosePositionMkt)),
            new TradingButtonItem(PositionActionEnum.ReverseByInstrument, 'panels.positions.buttons.reverse', this.isFiltered && this.isFilteredAction(PositionActionEnum.ReverseByInstrument)),
            new TradingButtonItem(PositionActionEnum.ReverseByIds, 'panels.positions.buttons.reverseSelected', this.isFiltered && this.isFilteredAction(PositionActionEnum.ReverseByIds)),
            new TradingButtonItem(PositionActionEnum.CLXByAccountAndInstrument, 'panels.positions.buttons.clxForInstrument', this.isFiltered && this.isFilteredAction(PositionActionEnum.CLXByAccountAndInstrument))
        ];
    }
    isFilteredAction(action) {
        switch (action) {
            case PositionActionEnum.CLXAll:
            case PositionActionEnum.ClosePositionMkt:
            case PositionActionEnum.ByAccountAndInstrument:
            case PositionActionEnum.ReverseByInstrument:
            case PositionActionEnum.ReverseByIds:
            case PositionActionEnum.CLXByAccountAndInstrument:
                return false;
            default:
                return true;
        }
    }
    updateAllowedActions() {
        if (isNullOrUndefined(this.Controls)) {
            return;
        }
        if (isNullOrUndefined(this.quickTableRactive)) {
            return;
        }
        const menuTagDict = this.menuTagDict;
        if (isNullOrUndefined(menuTagDict)) {
            return;
        }
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        const btns = this.Controls.btns;
        if (isNullOrUndefined(btns)) {
            return;
        }
        const selectedOrderIdArr = quickTable.selectedRowIds;
        const row = quickTable.rows[selectedOrderIdArr[0]];
        const allRowsIds = quickTable.getTableItemsIds();
        const allowedActionSet = DataCache.FOrderExecutor.getAllowedPositionActionSet(selectedOrderIdArr, allRowsIds, this.isFiltered);
        btns.updateEnability(allowedActionSet);
        const ins = !isNullOrUndefined(row) ? row.item.DisplayInstrumentName() : '';
        btns.setInstrument(PositionActionEnum.ByAccountAndInstrument, ins);
        btns.setInstrument(PositionActionEnum.CLXByAccountAndInstrument, ins);
        btns.setInstrument(PositionActionEnum.ReverseByInstrument, ins);
        const isAllowedPositionModifying = allowedActionSet[PositionActionEnum.PositionModifying];
        menuTagDict[PositionActionEnum.PositionModifying].enabled =
            isAllowedPositionModifying !== undefined && isAllowedPositionModifying.Allowed;
        const isAllowedModifyProductType = allowedActionSet[PositionActionEnum.ModifyProductType];
        menuTagDict[PositionActionEnum.ModifyProductType].enabled =
            isAllowedModifyProductType !== undefined && isAllowedModifyProductType.Allowed;
        menuTagDict[PositionActionEnum.ModifyProductType].visible = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING_MODE_MODIFICATION) && !Resources.isHidden('panel.positions.menu.ModifyProductType');
        const isAllowedExerciseOption = allowedActionSet[PositionActionEnum.ExerciseOption];
        const exerciseOptionMenuItem = menuTagDict[PositionActionEnum.ExerciseOption];
        exerciseOptionMenuItem.visible =
            !isNullOrUndefined(isAllowedExerciseOption) && (isAllowedExerciseOption === null || isAllowedExerciseOption === void 0 ? void 0 : isAllowedExerciseOption.Allowed); // Пункт отображается и активен, если в панели выбрана позиция по опциону с Exercise style = American.                // 93127
        // Пункт отображается и не активен, если в панели выбрано несколько позиций по опционам с Exercise style = American.  // https://docs.google.com/document/d/1GPd6aeAV_O25CrHPaRh_ixsSG0GpCHUx3JCU68QGPdI/edit
        exerciseOptionMenuItem.enabled = (isAllowedExerciseOption === null || isAllowedExerciseOption === void 0 ? void 0 : isAllowedExerciseOption.Allowed) && selectedOrderIdArr.length == 1;
        const isVisibleExerciseCancelOption = allowedActionSet[PositionActionEnum.ExerciseCancelVisibility];
        const isAllowedExerciseCancelOption = allowedActionSet[PositionActionEnum.ExerciseCancel];
        const exerciseCancelOptionMenuItem = menuTagDict[PositionActionEnum.ExerciseCancel];
        // Пункт отображается и активен, если в панели выбрана позиция по опциону со статусом Pending exercise.
        exerciseCancelOptionMenuItem.visible =
            !isNullOrUndefined(isVisibleExerciseCancelOption) && (isVisibleExerciseCancelOption === null || isVisibleExerciseCancelOption === void 0 ? void 0 : isVisibleExerciseCancelOption.Allowed);
        exerciseCancelOptionMenuItem.enabled = isAllowedExerciseCancelOption === null || isAllowedExerciseCancelOption === void 0 ? void 0 : isAllowedExerciseCancelOption.Allowed;
        exerciseCancelOptionMenuItem.disabledReason =
            isAllowedExerciseCancelOption !== undefined ? isAllowedExerciseCancelOption.Reason : undefined;
        const mutualCloseMenuItem = menuTagDict[PositionActionEnum.Mutual];
        mutualCloseMenuItem.visible = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_MUTUAL_CLOSE) && !Resources.isHidden('panel.positions.menu.MutualClose'); // #115483
        mutualCloseMenuItem.enabled = DataCache.isAllowedMutualCloseByPosIdArr(selectedOrderIdArr); // https://docs.google.com/document/d/1GnwZfiWpM7ilTS43hmXhlje1pwk8HIkoj3OypQRqeak/edit
        this.updateEnability(PositionActionEnum.ClosePositionMkt);
        this.updateEnability(PositionActionEnum.ClosePositionViaOE);
        const enabled = !!ins;
        super.SetOpeningPanelsCMEnability(menuTagDict, enabled);
    }
    // This is common logic for update enability for context menu items and hot buttons. USE THIS
    updateEnability(action) {
        var _a;
        const menuItem = this.menuTagDict[action];
        if (isNullOrUndefined(menuItem)) {
            return;
        }
        const selectedPositions = this.selectedPositions;
        let enabled = selectedPositions.length > 0;
        let tooltip = this.getTooltipForMenuItem(action);
        const img = (_a = this.getImgByAction(action)) !== null && _a !== void 0 ? _a : menuItem.svgImage;
        for (let i = 0; i < selectedPositions.length; i++) {
            let isAllowedResponce;
            switch (action) {
                case PositionActionEnum.ClosePositionMkt:
                    isAllowedResponce = IsAllowed.IsPositionClosingAllowed(selectedPositions[i], true);
                    break;
                case PositionActionEnum.ClosePositionViaOE:
                    isAllowedResponce = IsAllowed.IsPositionClosingAllowed(selectedPositions[i], false);
                    break;
            }
            if (!isNullOrUndefined(isAllowedResponce) && !isAllowedResponce.Allowed) {
                enabled = false;
                tooltip = isAllowedResponce.ReasonText;
                break;
            }
        }
        // Context menu
        menuItem.enabled = enabled;
        menuItem.svgImage = enabled ? img : 'menuError';
        menuItem.tooltip = tooltip;
        //Hot buttons
        const hotButtons = this.Controls.btns;
        if (!isNullOrUndefined(hotButtons)) {
            hotButtons.updateButtonEnability(action, enabled, tooltip);
        }
    }
    getImgByAction(action) {
        switch (action) {
            case PositionActionEnum.ClosePositionMkt:
                return 'closePositionByMkt';
            case PositionActionEnum.ClosePositionViaOE:
                return 'closeViaOE';
            default:
                return null;
        }
    }
    getTooltipForMenuItem(action) {
        switch (action) {
            case PositionActionEnum.ClosePositionViaOE:
                return 'panel.positions.CloseViaOE.FIFO';
            default:
                return null;
        }
    }
    action(action, confirm, placedFrom, numericLinkName) {
        if (!super.action(action, confirm, placedFrom, numericLinkName)) {
            return false;
        }
        switch (action) {
            case PositionActionEnum.ClosePositionMkt:
                this.closePositionsMkt(this.selectedPositions, placedFrom);
                return true;
        }
        const quickTable = super.getQuickTable();
        const selectedRowsIds = quickTable.selectedRowIds;
        const allRowsIds = quickTable.getTableItemsIds();
        void DataCache.FOrderExecutor.positionAction(action, this.isFiltered, selectedRowsIds, allRowsIds, confirm, placedFrom, numericLinkName);
        return true;
    }
    isClosingAction(action) {
        const closingActions = [
            PositionActionEnum.All,
            PositionActionEnum.ByAccountAndInstrument,
            PositionActionEnum.CLXAll,
            PositionActionEnum.CLXByAccountAndInstrument,
            PositionActionEnum.Long,
            PositionActionEnum.Short,
            PositionActionEnum.Positive,
            PositionActionEnum.Negative
        ];
        return closingActions.includes(action);
    }
    // #endregion
    // #endregion
    // из сохраненных properties восстанавливаем значения пунктов из контекстного меню панели
    initContextMenuItemsState() {
        var _a, _b, _c;
        this.UpdateShowTotalsStateChange(!!this.InitShowTotals);
        if ((_a = this.menuTagDict) === null || _a === void 0 ? void 0 : _a.ShowTotals) {
            this.menuTagDict.ShowTotals.checked = !!this.InitShowTotals;
        }
        if ((_b = this.menuTagDict) === null || _b === void 0 ? void 0 : _b.DisplayTrades) {
            const displayTradesMenuItem = this.menuTagDict.DisplayTrades;
            displayTradesMenuItem.checked = !!this.InitDisplayTrades;
            this.DisplayTradesStateChange(displayTradesMenuItem);
        }
        if ((_c = this.menuTagDict) === null || _c === void 0 ? void 0 : _c.ShowGroupQtyAs) {
            const showGroupQtyAsMenuItem = this.menuTagDict.ShowGroupQtyAs;
            if (showGroupQtyAsMenuItem.subitems) {
                const subItemTag = this.InitShowGropQtyAsTotal
                    ? PositionsPanel.ShowGroupQtyAs.Total
                    : PositionsPanel.ShowGroupQtyAs.Net;
                this.ShowGroupQtyAsStateChange(showGroupQtyAsMenuItem.subitems[subItemTag]); // #92796
            }
        }
    }
    // только нажатие левой кнопки мыши!
    onTableMouseDown(hittestInfo) {
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable) ||
            isNullOrUndefined(hittestInfo) ||
            !isValidNumber(hittestInfo.columnIndex) ||
            hittestInfo.columnIndex === -1) {
            return;
        }
        const columnIndex = quickTable.sortedColumns[hittestInfo.columnIndex].PRIVATE.index;
        const selectedItem = quickTable.getSelectedTableItems()[0];
        if (isNullOrUndefined(selectedItem) || !selectedItem.isAllowEdit(columnIndex)) {
            return;
        }
        const action = PositionActionEnum.PositionModifying;
        const placedFrom = PlacedFrom.WEB_POSITIONS_PANEL_DB_CLICK;
        switch (columnIndex) {
            case PositionItem.SL_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.StopLoss);
                break;
            case PositionItem.TP_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.TakeProfit);
                break;
            case PositionItem.PRODUCTTYPE_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.ProductType);
                break;
        }
    }
    createPositionItem(position) {
        return new PositionItem(position, SessionSettings, this.IsShowGroupQtyAsNet());
    }
    AddPositionEvent(position) {
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        quickTable.AddItem(this.createPositionItem(position));
        if (position.SuperPositionId != -1) {
            this.updateFIFOItems();
        }
        quickTable.needRedrawBackground = true;
    }
    RemovePositionEvent(position) {
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        quickTable.RemoveItem(PositionItem.GetItemId(position));
        quickTable.needRedrawBackground = true;
    }
    UpdatePositionEvent(position) {
        if (isNullOrUndefined(position)) {
            return;
        }
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        const row = quickTable.rows[position.PositionId];
        if (isNullOrUndefined(row)) {
            return;
        }
        const item = row.item;
        if (isNullOrUndefined(item)) {
            return;
        }
        const isDisabledBecauseOfExerciseOption = position.IsPendingExerciseOptionStatus();
        item.Disabled = isDisabledBecauseOfExerciseOption; // #93127 обновление серого состояния основного айтема position
        const superPositionID = position.SuperPositionId;
        if (superPositionID != -1) {
            if (item.superItemId !== superPositionID) {
                item.superItemId = superPositionID;
                this.updateFIFOItems();
            }
            this.UpdateTradesDisabledState(superPositionID, isDisabledBecauseOfExerciseOption); // #93127 обновление серого состояния subitem-ов position
        }
        quickTable.needRedrawBackground = true; // #92796
    }
    populateItems(positions) {
        for (const pos of positions) {
            this.AddPositionEvent(pos);
        }
    }
    // expand super position event (FIFO) #80010
    onExpandSuperPositionClick(data) {
        const superRow = data.row;
        const superPos = superRow.item.FPosition;
        const table = data.table;
        superRow.collapsedFIFO = !superRow.collapsedFIFO;
        const posKeys = Object.keys(table.rows);
        let needUpdate = false;
        for (let i = 0; i < posKeys.length; i++) {
            const row = table.rows[posKeys[i]];
            const pos = row.item.FPosition;
            const superID = pos.SuperPositionId;
            if (pos.PositionId != superID && superID == superPos.PositionId) {
                needUpdate = true;
                row.visible = superRow.collapsedFIFO;
            }
        }
        const qt = super.getQuickTable();
        if (needUpdate && qt) {
            qt.rowCountChanged = true;
            qt.updateScrollElementsCount();
        }
    }
    updateMenuItemsTooltip() {
        const menuTagDict = this.menuTagDict;
        if (isNullOrUndefined(menuTagDict)) {
            return;
        }
        const menuItem = menuTagDict[PositionActionEnum.ExerciseOption];
        if (!isNullOrUndefined(menuItem) && !menuItem.enabled) {
            menuItem.tooltip = Resources.getResource('panel.positions.menu.ExercisePositionDisabled.tt');
        }
        else {
            menuItem.tooltip = '';
        }
        const menuItemCancel = menuTagDict[PositionActionEnum.ExerciseCancel];
        // Пункт может отображаться и быть неактивным (тогда нужен тултип) по 2м причинам: 1) если в панели выбрано несколько позиций по опционам с Exercise style = American и Option exercise=Pending exercise. 2) если статус сессии не торговый(Trade session status у инструмента Allow operations -> Order entry = False) то не даем отменить исполнение.
        if (!isNullOrUndefined(menuItemCancel) && !menuItemCancel.enabled && menuItemCancel.disabledReason) {
            menuItemCancel.tooltip = Resources.getResource(menuItemCancel.disabledReason === IsAllowedResponceReason.MoreThanOnePosSelected
                ? 'panel.positions.menu.ExercisePositionDisabled.tt'
                : 'panel.positions.menu.CancelExerciseDisabledCuzTrSession.tt');
        }
        else {
            menuItemCancel.tooltip = '';
        }
    }
    IsShowGroupQtyAsNet() {
        return this.GetShowGroupQtyAsState() === PositionsPanel.ShowGroupQtyAs.Net;
    }
    GetShowGroupQtyAsState() {
        if (Resources.isHidden('panel.positions.menu.ShowGroupQtyAs')) {
            return PositionsPanel.ShowGroupQtyAs.Total;
        }
        if (!this.menuTagDict) {
            return null;
        }
        const menu = this.menuTagDict.ShowGroupQtyAs;
        if (!(menu === null || menu === void 0 ? void 0 : menu.subitems)) {
            return null;
        }
        const netItem = menu.subitems[PositionsPanel.ShowGroupQtyAs.Net];
        if (netItem === null || netItem === void 0 ? void 0 : netItem.checked) {
            return PositionsPanel.ShowGroupQtyAs.Net;
        }
        else {
            return PositionsPanel.ShowGroupQtyAs.Total;
        }
    }
    ShowGroupQtyAsStateChange(menuItem) {
        if (!this.menuTagDict) {
            return;
        }
        const menu = this.menuTagDict.ShowGroupQtyAs;
        if (!(menu === null || menu === void 0 ? void 0 : menu.subitems)) {
            return;
        }
        const subitems = menu.subitems;
        let itemToUnCheck = PositionsPanel.ShowGroupQtyAs.Total;
        if (menuItem.tag === itemToUnCheck) {
            itemToUnCheck = PositionsPanel.ShowGroupQtyAs.Net;
        }
        subitems[itemToUnCheck].checked = false;
        subitems[menuItem.tag].checked = true;
        this.UpdateCellsOnShowGroupQtyAsStateChange(menuItem.tag);
    }
    UpdateCellsOnShowGroupQtyAsStateChange(showGroupQtyAsValue) {
        if (Resources.isHidden('panel.positions.menu.ShowGroupQtyAs')) {
            return;
        }
        if (showGroupQtyAsValue === undefined) {
            showGroupQtyAsValue = this.GetShowGroupQtyAsState();
        }
        const netMode = showGroupQtyAsValue === PositionsPanel.ShowGroupQtyAs.Net;
        const qt = super.getQuickTable();
        if (!qt) {
            return;
        }
        for (let i = 0; i < qt.rowsArray.length; i++) {
            const row = qt.rowsArray[i];
            if (!row || row.isGroupRow || !row.item) {
                continue;
            }
            row.item.NetQty = netMode;
        }
        qt.needRedrawBackground = true;
    }
    // обновление disabled состояния item-ов по superPositionID
    UpdateTradesDisabledState(superID, isDisabled) {
        isDisabled = !!isDisabled;
        const qt = super.getQuickTable();
        if (!qt) {
            return;
        }
        for (let i = 0; i < qt.rowsArray.length; i++) {
            const row = qt.rowsArray[i];
            const item = row.item;
            if (item && item.superItemId == superID) {
                item.Disabled = isDisabled;
            }
        }
    }
    DisplayTradesStateChange(menuItem) {
        this.UpdateTradesVisibility(menuItem.checked);
    }
    UpdateTradesVisibility(state) {
        const qt = super.getQuickTable();
        if (!qt) {
            return;
        }
        qt.additionalFilter = state;
        const collapsed = {};
        for (let i = 0; i < qt.rowsArray.length; i++ // trades rows visibiility change here
        ) {
            const row = qt.rowsArray[i];
            const item = row.item;
            if (item === null || item === void 0 ? void 0 : item.superItemId) {
                row.visible = state || item.ItemId == item.superItemId;
                if (!item.isSubItem) // запоминание collapsed состояния супер позиций
                 {
                    collapsed[item.ItemId] = row.collapsedFIFO;
                }
                else if (state) {
                    row.visible = collapsed[item.superItemId];
                } // применение видимости для sub позиций
            }
        }
        const lS = LinkedSystem;
        if (lS.accLinkingActive) {
            const accId = lS.accountStorage[Object.keys(lS.accountStorage)[0]];
            qt.filterByAccountNumber(accId); // отфильтруем по аккаунту позиции, если раскрылся лишний трейд
        }
        qt.needRedrawBackground = true;
        qt.rowCountChanged = true;
        qt.updateScrollElementsCount();
    }
    updateFIFOItems() {
        const me = this;
        const qt = me.quickTableRactive.quickTable;
        if (!qt) {
            return;
        }
        if (me.regroupFIFOTimerID) {
            clearTimeout(me.regroupFIFOTimerID);
        }
        me.regroupFIFOTimerID = setTimeout(function () {
            qt.regroupSuperItems(); // enough to do this one time when all positions are added (here for DataCache AddPosition event) (FIFO) #80010
            me.UpdateTradesVisibility(qt.additionalFilter);
        }, 0);
    }
}
PositionsPanel.ShowGroupQtyAs = {
    Total: 0,
    Net: 1
};
